import type { Animation } from '@ionic/core';
import { createAnimation as IonicCreateAnimation } from '@ionic/core/dist/esm';

export function createAnimation(...args: any): Animation {
  const ionicAnimation = IonicCreateAnimation(...args);
  const ionicAnimationWithDefaultWebAnimationsConfigs = ionicAnimation.fill('none');
  return ionicAnimationWithDefaultWebAnimationsConfigs;
}

// TODO: Transformar em Token de Easin conforme documentação do Material Design 3
// TODO: https://m3.material.io/styles/motion/easing-and-duration/applying-easing-and-duration
export const DURATION_SHORT_1 = 50;
export const DURATION_SHORT_2 = 100;
export const DURATION_SHORT_3 = 150;
export const DURATION_SHORT_4 = 200;
export const DURATION_MEDIUM_1 = 250;
export const DURATION_MEDIUM_2 = 300;
export const DURATION_MEDIUM_3 = 350;
export const DURATION_MEDIUM_4 = 400;
export const DURATION_LONG_1 = 450;
export const DURATION_LONG_2 = 500;
export const DURATION_LONG_3 = 550;
export const DURATION_LONG_4 = 600;
export const DEFAULT_DURATION = DURATION_SHORT_4;

export const DEFAULT_TRANSFORM_ORIGIN = 'top left';
export const EASING_EMPHASIZED_ACCELERATE = 'cubic-bezier(0.3, 0, 0.8, 0.15)';
export const EASING_EMPHASIZED_DECCELERATE = 'cubic-bezier(0.05, 0.7, 0.1, 1)';

// String value to disable animations inside animations props
export const DISABLE_ANIMATION_VALUE = 'none';

export function fadeIn(): Animation {
  return createAnimation().duration(150).fromTo('opacity', '0', '1');
}

export function fadeOut(): Animation {
  return fadeIn().direction('reverse');
}

export type FromTo = {
  from: string;
  to: string;
};

export function scale(scaleOptions?: FromTo): Animation | undefined {
  if (!scaleOptions || !scaleOptions.from || !scaleOptions.to) {
    console.error("Error: the fromTo options were not specified in the 'scale' animation");
    return;
  }

  return createAnimation()
    .duration(DEFAULT_DURATION)
    .beforeStyles({ 'transform-origin': DEFAULT_TRANSFORM_ORIGIN })
    .keyframes([
      { offset: 0, transform: `scale(${scaleOptions.from})`, composite: 'add' },
      { offset: 1, transform: `scale(${scaleOptions.to})`, composite: 'add' },
    ]);
}

export function rotate(rotateOptions?: FromTo): Animation | undefined {
  if (!rotateOptions || !rotateOptions.from || !rotateOptions.to) {
    console.error("Error: the fromTo options were not specified in the 'rotate' animation");
    return;
  }
  return createAnimation()
    .duration(DEFAULT_DURATION)
    .beforeStyles({ 'transform-origin': DEFAULT_TRANSFORM_ORIGIN })
    .keyframes([
      { offset: 0, transform: `rotate(${rotateOptions.from})`, composite: 'add' },
      { offset: 1, transform: `rotate(${rotateOptions.to})`, composite: 'add' },
    ]);
}

export function translate(translateOptions?: FromTo): Animation | undefined {
  if (!translateOptions || !translateOptions.from || !translateOptions.to) {
    console.error("Error: the fromTo options were not specified in the 'translate' animation");
    return;
  }
  return createAnimation()
    .duration(DEFAULT_DURATION)
    .beforeStyles({ 'transform-origin': DEFAULT_TRANSFORM_ORIGIN })
    .keyframes([
      { offset: 0, transform: `translate(${translateOptions.from})`, composite: 'add' },
      { offset: 1, transform: `translate(${translateOptions.to})`, composite: 'add' },
    ]);
}

export function slideIn(direction: 'left' | 'right' = 'left'): Animation {
  const slideAnimation = createAnimation().duration(DEFAULT_DURATION);

  switch (direction) {
    case 'left':
      slideAnimation.beforeStyles({ 'transform-origin': 'left' }).keyframes([
        { transform: 'translateX(-100%)', composite: 'add' },
        { transform: 'translateX(0%)', composite: 'add' },
      ]);
      break;

    case 'right':
      slideAnimation.beforeStyles({ 'transform-origin': 'right' }).keyframes([
        { transform: 'translateX(100%)', composite: 'add' },
        { transform: 'translateX(0%)', composite: 'add' },
      ]);
      break;
  }

  return slideAnimation;
}

export function maxHeight(heightOptions?: FromTo): Animation | undefined {
  if (!heightOptions || !heightOptions.from || !heightOptions.to) {
    console.error("Error: the fromTo options were not specified in the 'maxHeight' animation");
    return;
  }
  return createAnimation()
    .duration(DEFAULT_DURATION)
    .keyframes([
      { offset: 0, maxHeight: heightOptions.from },
      { offset: 1, maxHeight: heightOptions.to },
    ]);
}

export function growY(heightOptions?: FromTo): Animation | undefined {
  if (!heightOptions || !heightOptions.from || !heightOptions.to) {
    console.error("Error: the fromTo options were not specified in the 'growY' animation");
    return;
  }
  return createAnimation()
    .duration(DEFAULT_DURATION)
    .keyframes([
      { offset: 0, maxHeight: heightOptions.from },
      { offset: 1, maxHeight: heightOptions.to },
    ])
    .beforeStyles({ overflow: 'hidden' })
    .afterClearStyles(['overflow']);
}

export function growX(options?: FromTo): Animation | undefined {
  if (!options || !options.from || !options.to) {
    console.error("Error: the fromTo options were not specified in the 'growX' animation");
    return;
  }
  return createAnimation()
    .duration(DEFAULT_DURATION)
    .keyframes([
      { offset: 0, maxWidth: options.from },
      { offset: 1, maxWidth: options.to },
    ])
    .beforeStyles({ overflow: 'hidden' })
    .afterClearStyles(['overflow']);
}

export function maxWidth(widthOptions?: FromTo): Animation | undefined {
  if (!widthOptions || !widthOptions.from || !widthOptions.to) {
    console.error("Error: the fromTo options were not specified in the 'maxWidth' animation");
    return;
  }
  return createAnimation()
    .duration(DEFAULT_DURATION)
    .keyframes([
      { offset: 0, maxWidth: widthOptions.from },
      { offset: 1, maxWidth: widthOptions.to },
    ]);
}
